import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  AccountCreatedEnvelope,
  AccountPointsUpdatedEnvelope,
  AccountRewardAvailabilityUpdatedEnvelope,
  AccountUpdatedEnvelope,
  AdjustPointsOptions,
  AdjustPointsResponse,
  BulkAdjustPointsOptions,
  BulkAdjustPointsResponse,
  CountAccountsOptions,
  CountAccountsResponse,
  CreateAccountResponse,
  EarnPointsOptions,
  EarnPointsResponse,
  GetAccountBySecondaryIdOptions,
  GetAccountBySecondaryIdResponse,
  GetCurrentMemberAccountResponse,
  GetProgramTotalsResponse,
  ListAccountsOptions,
  ListAccountsResponse,
  LoyaltyAccount,
  LoyaltyAccountQuery,
  LoyaltyAccountSearch,
  LoyaltyAccountsQueryBuilder,
  QueryLoyaltyAccountsResponse,
  SearchAccountsResponse,
  adjustPoints as universalAdjustPoints,
  bulkAdjustPoints as universalBulkAdjustPoints,
  countAccounts as universalCountAccounts,
  createAccount as universalCreateAccount,
  earnPoints as universalEarnPoints,
  getAccount as universalGetAccount,
  getAccountBySecondaryId as universalGetAccountBySecondaryId,
  getCurrentMemberAccount as universalGetCurrentMemberAccount,
  getProgramTotals as universalGetProgramTotals,
  listAccounts as universalListAccounts,
  queryLoyaltyAccounts as universalQueryLoyaltyAccounts,
  searchAccounts as universalSearchAccounts,
  typedQueryLoyaltyAccounts as universalTypedQueryLoyaltyAccounts,
} from './loyalty-v1-account-accounts.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/loyalty' };

export function createAccount(httpClient: HttpClient): CreateAccountSignature {
  return (contactId: string) =>
    universalCreateAccount(
      contactId,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateAccountSignature {
  /**
   * Creates a loyalty account for a site contact.
   *
   * To create a new loyalty account, a customer must first be a site contact with a contact ID, see the Contacts API.
   * The site must also have an [active loyalty program](https://dev.wix.com/docs/rest/crm/loyalty-program/program/activate-loyalty-program)
   * before loyalty accounts can be created.
   * @param - Contact ID for a site's contact. See the Contacts API for more information.
   */
  (contactId: string): Promise<
    NonNullablePaths<
      CreateAccountResponse,
      | `account._id`
      | `account.contactId`
      | `account.points.balance`
      | `account.points.earned`
      | `account.points.adjusted`
      | `account.points.redeemed`
      | `account.points.expired`
      | `account.rewardAvailable`
      | `account.revision`
      | `account.tier.points`
      | `account.contact.picture._id`
      | `account.contact.picture.url`
      | `account.contact.picture.height`
      | `account.contact.picture.width`
      | `account.pointsExpiration.expiringPointsAmount`,
      5
    >
  >;
}

export function earnPoints(httpClient: HttpClient): EarnPointsSignature {
  return (
    accountId: string,
    options?: NonNullablePaths<EarnPointsOptions, `appId` | `idempotencyKey`, 2>
  ) =>
    universalEarnPoints(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface EarnPointsSignature {
  /**
   * Adds points to a loyalty account.
   *
   * Only a positive amount can be added using this method. To adjust an account's balance
   * for a negative amount, use Adjust Points.
   *
   * This method allows customers to manually earn points to their loyalty accounts. To use this method you must include an `appId` and an `idempotencyKey`.
   * Any string can be set as the `appId` or `idempotencyKey`. In contrast to when an account earns points through an action taken on your site, then,
   * the `appId` automatically sets to the source app that generates the points. In both cases, the transaction `type` is `"EARN"`.
   * @param - Loyalty account ID.
   * @param - Earn points info.
   */
  (
    accountId: string,
    options?: NonNullablePaths<EarnPointsOptions, `appId` | `idempotencyKey`, 2>
  ): Promise<
    NonNullablePaths<
      EarnPointsResponse,
      | `account._id`
      | `account.contactId`
      | `account.points.balance`
      | `account.points.earned`
      | `account.points.adjusted`
      | `account.points.redeemed`
      | `account.points.expired`
      | `account.rewardAvailable`
      | `account.revision`
      | `account.tier.points`
      | `account.contact.picture._id`
      | `account.contact.picture.url`
      | `account.contact.picture.height`
      | `account.contact.picture.width`
      | `account.pointsExpiration.expiringPointsAmount`
      | `transactionId`,
      5
    >
  >;
}

export function adjustPoints(httpClient: HttpClient): AdjustPointsSignature {
  return (accountId: string, options?: AdjustPointsOptions) =>
    universalAdjustPoints(
      accountId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface AdjustPointsSignature {
  /**
   * Adjusts the point balance of a loyalty account.
   *
   * You can adjust the balance in two ways:
   * - `balance` allows you to set the total points balance to this new amount. The transaction `type` will return as `"ADJUST"`.
   * - `amount` allows you to adjust the points balance by this amount. This amount can be a positive number to increase the points balance or a negative number to decrease the balance. The transaction `type` will return as `"GIVE"`.
   *
   * An account may not be adjusted to result in a negative balance. If you pass an integer in both the `balance` and the `amount` parameters then the `balance` adjustment takes effect and the
   * `amount` adjustment is ignored.
   * @param - Loyalty account ID.
   * @param - Options to use when adjusting points.
   */
  (accountId: string, options?: AdjustPointsOptions): Promise<
    NonNullablePaths<
      AdjustPointsResponse,
      | `account._id`
      | `account.contactId`
      | `account.points.balance`
      | `account.points.earned`
      | `account.points.adjusted`
      | `account.points.redeemed`
      | `account.points.expired`
      | `account.rewardAvailable`
      | `account.revision`
      | `account.tier.points`
      | `account.contact.picture._id`
      | `account.contact.picture.url`
      | `account.contact.picture.height`
      | `account.contact.picture.width`
      | `account.pointsExpiration.expiringPointsAmount`
      | `transactionId`,
      5
    >
  >;
}

export function getAccount(httpClient: HttpClient): GetAccountSignature {
  return (_id: string) =>
    universalGetAccount(
      _id,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAccountSignature {
  /**
   * Retrieves a loyalty account by ID.
   *
   * You can also retrieve an account using a secondary ID, such as a contact ID or a member ID by calling
   * Get Account by Secondary ID.
   * @param - ID of the account to retrieve.
   * @returns Retrieved loyalty account.
   */
  (_id: string): Promise<
    NonNullablePaths<
      LoyaltyAccount,
      | `_id`
      | `contactId`
      | `points.balance`
      | `points.earned`
      | `points.adjusted`
      | `points.redeemed`
      | `points.expired`
      | `rewardAvailable`
      | `revision`
      | `tier.points`
      | `contact.picture._id`
      | `contact.picture.url`
      | `contact.picture.height`
      | `contact.picture.width`
      | `pointsExpiration.expiringPointsAmount`,
      4
    >
  >;
}

export function queryLoyaltyAccounts(
  httpClient: HttpClient
): QueryLoyaltyAccountsSignature {
  return () =>
    universalQueryLoyaltyAccounts(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryLoyaltyAccountsSignature {
  /**
   * Creates a query to retrieve a list of accounts.
   *
   * The `queryLoyaltyAccounts()` method builds a query to retrieve a list of events and returns a `LoyaltyAccountsQueryBuilder` object.
   *
   * The returned object contains the query definition, which is typically used to run the query using the `find()` method.
   *
   * You can refine the query by chaining `LoyaltyAccountsQueryBuilder` methods onto the query. `LoyaltyAccountsQueryBuilder` methods enable you to sort, filter, and control the results `queryLoyaltyAccounts()` returns.
   *
   * `queryLoyaltyAccounts()` runs with these `LoyaltyAccountsQueryBuilder` defaults, which you can override:
   *
   * - `skip(0)`
   * - `limit(50)`
   * - `descending("_createdDate")`
   *
   * The methods that are chained to `queryLoyaltyAccounts()` are applied in the order they're called. For example, if you apply `ascending("points.balance")` and then `descending("points.earned")`, the results are sorted first by the balance, and then, if there are multiple results with the same balance, the items are sorted by earned points.
   */
  (): LoyaltyAccountsQueryBuilder;
}

export function typedQueryLoyaltyAccounts(
  httpClient: HttpClient
): TypedQueryLoyaltyAccountsSignature {
  return (query: LoyaltyAccountQuery) =>
    universalTypedQueryLoyaltyAccounts(
      query,
      // @ts-ignore
      { httpClient }
    );
}

interface TypedQueryLoyaltyAccountsSignature {
  /** */
  (query: LoyaltyAccountQuery): Promise<
    NonNullablePaths<
      QueryLoyaltyAccountsResponse,
      | `loyaltyAccounts`
      | `loyaltyAccounts.${number}._id`
      | `loyaltyAccounts.${number}.contactId`
      | `loyaltyAccounts.${number}.points.balance`
      | `loyaltyAccounts.${number}.points.earned`
      | `loyaltyAccounts.${number}.points.adjusted`
      | `loyaltyAccounts.${number}.points.redeemed`
      | `loyaltyAccounts.${number}.points.expired`
      | `loyaltyAccounts.${number}.rewardAvailable`
      | `loyaltyAccounts.${number}.revision`
      | `loyaltyAccounts.${number}.tier.points`
      | `loyaltyAccounts.${number}.contact.picture._id`
      | `loyaltyAccounts.${number}.contact.picture.url`
      | `loyaltyAccounts.${number}.contact.picture.height`
      | `loyaltyAccounts.${number}.contact.picture.width`
      | `loyaltyAccounts.${number}.pointsExpiration.expiringPointsAmount`,
      6
    >
  >;
}

export function getProgramTotals(
  httpClient: HttpClient
): GetProgramTotalsSignature {
  return () =>
    universalGetProgramTotals(
      // @ts-ignore
      { httpClient }
    );
}

interface GetProgramTotalsSignature {
  /**
   * Retrieves the total amount of points earned, redeemed, and adjusted for the entire loyalty program.
   *
   * The `balance` is the current total of points outstanding, while the `earned`, `adjusted`, and `redeemed` amounts
   * are the all-time accumulated amounts. The totals include the amounts for all loyalty accounts.
   */
  (): Promise<
    NonNullablePaths<
      GetProgramTotalsResponse,
      | `points.balance`
      | `points.earned`
      | `points.adjusted`
      | `points.redeemed`
      | `points.expired`
      | `tierTotals`
      | `tierTotals.${number}.numberOfAccounts`,
      4
    >
  >;
}

export function getCurrentMemberAccount(
  httpClient: HttpClient
): GetCurrentMemberAccountSignature {
  return () =>
    universalGetCurrentMemberAccount(
      // @ts-ignore
      { httpClient }
    );
}

interface GetCurrentMemberAccountSignature {
  /**
   * Retrieves the current member's loyalty account.
   */
  (): Promise<
    NonNullablePaths<
      GetCurrentMemberAccountResponse,
      | `account._id`
      | `account.contactId`
      | `account.points.balance`
      | `account.points.earned`
      | `account.points.adjusted`
      | `account.points.redeemed`
      | `account.points.expired`
      | `account.rewardAvailable`
      | `account.revision`
      | `account.tier.points`
      | `account.contact.picture._id`
      | `account.contact.picture.url`
      | `account.contact.picture.height`
      | `account.contact.picture.width`
      | `account.pointsExpiration.expiringPointsAmount`,
      5
    >
  >;
}

export function getAccountBySecondaryId(
  httpClient: HttpClient
): GetAccountBySecondaryIdSignature {
  return (options: GetAccountBySecondaryIdOptions) =>
    universalGetAccountBySecondaryId(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetAccountBySecondaryIdSignature {
  /**
   * Retrieves the account belonging to the specified contact or member.
   *
   * This method retrieves loyalty accounts using either a customer's contact ID or member ID. You can also retrieve an account
   * using the loyalty account ID with Get Account.
   * @param - ID of the customer to retrieve loyalty account for.
   */
  (options: GetAccountBySecondaryIdOptions): Promise<
    NonNullablePaths<
      GetAccountBySecondaryIdResponse,
      | `account._id`
      | `account.contactId`
      | `account.points.balance`
      | `account.points.earned`
      | `account.points.adjusted`
      | `account.points.redeemed`
      | `account.points.expired`
      | `account.rewardAvailable`
      | `account.revision`
      | `account.tier.points`
      | `account.contact.picture._id`
      | `account.contact.picture.url`
      | `account.contact.picture.height`
      | `account.contact.picture.width`
      | `account.pointsExpiration.expiringPointsAmount`,
      5
    >
  >;
}

export function listAccounts(httpClient: HttpClient): ListAccountsSignature {
  return (options?: ListAccountsOptions) =>
    universalListAccounts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListAccountsSignature {
  /**
   * Retrieves a list of accounts, given the provided filters.
   *
   * You can retrieve selected loyalty accounts with an array of `contactIds` or retrieve a list of all of a site's loyalty accounts
   * with an empty request parameter. Use the `cursorPaging` parameters to limit how many items load at a time.
   *
   * Deprecated method, please use Query Loyalty Accounts or Search Accounts instead.
   * @param - Options to use when retrieving a list of loyalty accounts.
   * @deprecated
   */
  (options?: ListAccountsOptions): Promise<
    NonNullablePaths<
      ListAccountsResponse,
      | `accounts`
      | `accounts.${number}._id`
      | `accounts.${number}.contactId`
      | `accounts.${number}.points.balance`
      | `accounts.${number}.points.earned`
      | `accounts.${number}.points.adjusted`
      | `accounts.${number}.points.redeemed`
      | `accounts.${number}.points.expired`
      | `accounts.${number}.rewardAvailable`
      | `accounts.${number}.revision`
      | `accounts.${number}.tier.points`
      | `accounts.${number}.contact.picture._id`
      | `accounts.${number}.contact.picture.url`
      | `accounts.${number}.contact.picture.height`
      | `accounts.${number}.contact.picture.width`
      | `accounts.${number}.pointsExpiration.expiringPointsAmount`,
      6
    >
  >;
}

export function searchAccounts(
  httpClient: HttpClient
): SearchAccountsSignature {
  return (search: LoyaltyAccountSearch) =>
    universalSearchAccounts(
      search,
      // @ts-ignore
      { httpClient }
    );
}

interface SearchAccountsSignature {
  /**
   * Retrieves a list of accounts, given the provided filters and search capabilities.
   * Search is executed on the account's name and email values.
   * @param - Search options.
   * @returns Accounts found through provided search capabilities, along with paging and aggregation data of the results.
   */
  (search: LoyaltyAccountSearch): Promise<
    NonNullablePaths<
      SearchAccountsResponse,
      | `accounts`
      | `accounts.${number}._id`
      | `accounts.${number}.contactId`
      | `accounts.${number}.points.balance`
      | `accounts.${number}.points.earned`
      | `accounts.${number}.points.adjusted`
      | `accounts.${number}.points.redeemed`
      | `accounts.${number}.points.expired`
      | `accounts.${number}.rewardAvailable`
      | `accounts.${number}.revision`
      | `accounts.${number}.tier.points`
      | `accounts.${number}.contact.picture._id`
      | `accounts.${number}.contact.picture.url`
      | `accounts.${number}.contact.picture.height`
      | `accounts.${number}.contact.picture.width`
      | `accounts.${number}.pointsExpiration.expiringPointsAmount`
      | `aggregationData.results`
      | `aggregationData.results.${number}.scalar.type`
      | `aggregationData.results.${number}.scalar.value`
      | `aggregationData.results.${number}.scalarDate.type`
      | `aggregationData.results.${number}.scalarDate.value`
      | `aggregationData.results.${number}.name`
      | `aggregationData.results.${number}.type`
      | `aggregationData.results.${number}.fieldPath`,
      6
    >
  >;
}

export function countAccounts(httpClient: HttpClient): CountAccountsSignature {
  return (options?: CountAccountsOptions) =>
    universalCountAccounts(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface CountAccountsSignature {
  /**
   * Retrieves the count of found accounts, given the provided filters and search capabilities.
   *
   * Use with Search Accounts to retrieve the total amount of loyalty accounts on a site.
   * @returns Amount of accounts found for the given search query
   */
  (options?: CountAccountsOptions): Promise<
    NonNullablePaths<CountAccountsResponse, `count`, 2>
  >;
}

export function bulkAdjustPoints(
  httpClient: HttpClient
): BulkAdjustPointsSignature {
  return (options?: BulkAdjustPointsOptions) =>
    universalBulkAdjustPoints(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkAdjustPointsSignature {
  /**
   * Updates the points balance of multiple accounts.
   *
   * You can adjust the balance in two ways:
   * - `balance` allows you to set the total points balance to this new amount. The transaction `type` will return as `"ADJUST"`.
   * - `amount` allows you to adjust the points balance by this amount. This amount can be a positive number to increase the points balance or a negative number to decrease the balance. The transaction `type` will return as `"GIVE"`.
   *
   * You can use the job ID from the response to call [Get Async Job](https://dev.wix.com/docs/api-reference/business-management/async-job/get-async-job)
   * to retrieve the job details and metadata.
   */
  (options?: BulkAdjustPointsOptions): Promise<BulkAdjustPointsResponse>;
}

export const onAccountCreated = EventDefinition(
  'wix.loyalty.v1.account_created',
  true,
  (event: AccountCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.lastActivityDate' },
            { path: 'entity.tier.updatedDate' },
            { path: 'entity.tier.recalculationDate' },
            { path: 'entity.pointsExpiration.expirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AccountCreatedEnvelope>();
export const onAccountPointsUpdated = EventDefinition(
  'wix.loyalty.v1.account_points_updated',
  true,
  (event: AccountPointsUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'data.account.createdDate' },
            { path: 'data.account.updatedDate' },
            { path: 'data.account.lastActivityDate' },
            { path: 'data.account.tier.updatedDate' },
            { path: 'data.account.tier.recalculationDate' },
            { path: 'data.account.pointsExpiration.expirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AccountPointsUpdatedEnvelope>();
export const onAccountRewardAvailabilityUpdated = EventDefinition(
  'wix.loyalty.v1.account_reward_availability_updated',
  true,
  (event: AccountRewardAvailabilityUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [{ path: 'metadata.eventTime' }],
        },
      ])
    )
)<AccountRewardAvailabilityUpdatedEnvelope>();
export const onAccountUpdated = EventDefinition(
  'wix.loyalty.v1.account_updated',
  true,
  (event: AccountUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'entity.lastActivityDate' },
            { path: 'entity.tier.updatedDate' },
            { path: 'entity.tier.recalculationDate' },
            { path: 'entity.pointsExpiration.expirationDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<AccountUpdatedEnvelope>();

export {
  AccountCreatedEnvelope,
  AccountInfo,
  AccountInfoMetadata,
  AccountPointsUpdatedEnvelope,
  AccountRewardAvailabilityUpdatedEnvelope,
  AccountToUpsert,
  AccountUpdatedEnvelope,
  ActionEvent,
  AdjustPointsOptions,
  AdjustPointsOptionsTypeOneOf,
  AdjustPointsRequest,
  AdjustPointsRequestTypeOneOf,
  AdjustPointsResponse,
  Aggregation,
  AggregationData,
  AggregationKindOneOf,
  AggregationResults,
  AggregationResultsResultOneOf,
  AggregationResultsScalarDateResult,
  AggregationResultsScalarResult,
  AggregationType,
  ApplicationError,
  BaseEventMetadata,
  BillingReference,
  BulkAccountResult,
  BulkActionMetadata,
  BulkAdjustPointsOptions,
  BulkAdjustPointsOptionsTypeOneOf,
  BulkAdjustPointsRequest,
  BulkAdjustPointsRequestTypeOneOf,
  BulkAdjustPointsResponse,
  BulkUpsertAccountsRequest,
  BulkUpsertAccountsResponse,
  CancellationDetails,
  Contact,
  ContactSyncRequest,
  ContractSwitchReason,
  ContractSwitchType,
  ContractSwitched,
  CountAccountsOptions,
  CountAccountsRequest,
  CountAccountsResponse,
  CreateAccountRequest,
  CreateAccountResponse,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  CursorSearch,
  CursorSearchPagingMethodOneOf,
  Cursors,
  Cycle,
  CycleCycleSelectorOneOf,
  DateHistogramAggregation,
  DateHistogramAggregationInterval,
  DateHistogramResult,
  DateHistogramResults,
  DeductPointsRequest,
  DeductPointsResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  EarnPointsOptions,
  EarnPointsOptionsActivityDetailsOneOf,
  EarnPointsRequest,
  EarnPointsRequestActivityDetailsOneOf,
  EarnPointsResponse,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExportAccountsRequest,
  ExportAccountsResponse,
  FirstTransaction,
  FocalPoint,
  FollowedSocialMedia,
  GetAccountBySecondaryIdOptions,
  GetAccountBySecondaryIdOptionsIdOneOf,
  GetAccountBySecondaryIdRequest,
  GetAccountBySecondaryIdRequestIdOneOf,
  GetAccountBySecondaryIdResponse,
  GetAccountRequest,
  GetAccountResponse,
  GetCurrentMemberAccountRequest,
  GetCurrentMemberAccountResponse,
  GetProgramTotalsRequest,
  GetProgramTotalsResponse,
  GroupByAggregation,
  GroupByAggregationKindOneOf,
  GroupByValueResults,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  IncludeMissingValuesOptions,
  Initiator,
  Interval,
  IntervalUnit,
  ItemMetadata,
  ListAccountsOptions,
  ListAccountsRequest,
  ListAccountsResponse,
  ListUserAccountsRequest,
  ListUserAccountsResponse,
  LoyaltyAccount,
  LoyaltyAccountForMetaSite,
  LoyaltyAccountQuerySpec,
  LoyaltyAccountSearchSpec,
  LoyaltyAccountsQueryBuilder,
  LoyaltyAccountsQueryResult,
  MessageEnvelope,
  MissingValues,
  Mode,
  NestedAggregation,
  NestedAggregationItem,
  NestedAggregationItemKindOneOf,
  NestedAggregationResults,
  NestedAggregationResultsResultOneOf,
  NestedAggregationType,
  NestedResultValue,
  NestedResultValueResultOneOf,
  NestedResults,
  NestedValueAggregationResult,
  OneTime,
  Paging,
  PagingMetadataV2,
  Points,
  PointsExpiration,
  PointsUpdated,
  PriceIncreaseTrigger,
  ProductAdjustment,
  ProductPriceIncreaseData,
  ProviderName,
  QueryLoyaltyAccountsRequest,
  QueryLoyaltyAccountsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RangeAggregation,
  RangeAggregationResult,
  RangeBucket,
  RangeResult,
  RangeResults,
  ReactivationData,
  ReactivationReasonEnum,
  RecurringChargeAttemptFailed,
  RecurringChargeSucceeded,
  RedeemDeterminedAmountOfPointsRequest,
  RedeemDeterminedAmountOfPointsResponse,
  RedeemPointsRequest,
  RedeemPointsResponse,
  RefundTransactionRequest,
  RefundTransactionResponse,
  RefundType,
  RestoreInfo,
  Results,
  RewardAvailabilityUpdated,
  RollingWindow,
  ScalarAggregation,
  ScalarDateResult,
  ScalarResult,
  ScalarType,
  SearchAccountsRequest,
  SearchAccountsResponse,
  SearchDetails,
  SortDirection,
  SortOrder,
  SortType,
  Sorting,
  Status,
  Subscription,
  SubscriptionAssigned,
  SubscriptionAutoRenewTurnedOff,
  SubscriptionAutoRenewTurnedOn,
  SubscriptionCancelled,
  SubscriptionCreated,
  SubscriptionEvent,
  SubscriptionEventEventOneOf,
  SubscriptionMigratedFromVista,
  SubscriptionNearEndOfPeriod,
  SubscriptionPendingChange,
  SubscriptionStatus,
  SubscriptionTransferred,
  SubscriptionUnassigned,
  SubscriptionUpdated,
  SubscriptionVistaMigrationReverted,
  Tier,
  TierDefinition,
  TierTotal,
  TiersProgramSettings,
  TiersProgramSettingsChanged,
  TiersProgramSettingsPeriodOneOf,
  TiersRollingUpdate,
  UnassignReason,
  UpdateTrigger,
  ValueAggregation,
  ValueAggregationOptionsOneOf,
  ValueAggregationResult,
  ValueResult,
  ValueResults,
  WebhookIdentityType,
  utils,
} from './loyalty-v1-account-accounts.universal.js';
